/**
 * New Items Tabs Controller
 *
 * Handles tab switching for the "Nieuw op LastDodo" homepage section.
 * Supports both horizontal tabs (desktop) and select dropdown (mobile).
 * Shows/hides corresponding swiper containers and updates CTA button text.
 * Directly manages Swiper instances for proper initialization when panels become visible.
 *
 * Usage:
 *   - Add data-controller="new-items-tabs" to the section container
 *   - Add data-new-items-tabs-target="tab" to each desktop tab button
 *   - Add data-new-items-tabs-target="select" to the mobile select dropdown
 *   - Add data-new-items-tabs-target="panel" to each swiper panel container
 *   - Add data-new-items-tabs-target="cta" to the CTA button
 *   - Add data-action="click->new-items-tabs#switchTab" to desktop tabs
 *   - Add data-action="change->new-items-tabs#switchSelect" to mobile select
 *   - Add data-new-items-tabs-index-param="0" (index) to tabs/options
 *   - Add data-new-items-tabs-label-param="Te koop" (label) for CTA text
 *   - Add data-new-items-tabs-url-param="..." for CTA link
 */
import {Controller} from 'stimulus';
import Swiper, {Navigation, Lazy, Keyboard} from 'swiper';

// Enable required Swiper modules
Swiper.use([Navigation, Lazy, Keyboard]);

export default class extends Controller {
  static targets = ['tab', 'select', 'panel', 'cta', 'ctaText'];

  /**
   * Initializes the controller.
   * Sets default active state on first tab/panel and initializes first Swiper.
   */
  connect() {
    // Store Swiper instances by panel index
    this.swipers = {};

    // Ensure first tab and panel are active on connect
    this.activateIndex(0);

    // Set initial CTA based on first tab
    if (this.tabTargets.length > 0) {
      const firstTab = this.tabTargets[0];
      const label = firstTab.dataset.newItemsTabsLabelParam || '';
      const url = firstTab.dataset.newItemsTabsUrlParam || '#';
      this.updateCta(label, url);
    }

    // Initialize Swiper for first (active) panel
    this.initializeSwiperForPanel(0);
  }

  /**
   * Cleanup when controller disconnects.
   * Destroys all Swiper instances to prevent memory leaks.
   */
  disconnect() {
    Object.values(this.swipers).forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    this.swipers = {};
  }

  /**
   * Handles click on desktop tab buttons.
   * Uses dataset attributes for compatibility with Stimulus 1.x
   *
   * @param {Event} e - Click event from tab button
   */
  switchTab(e) {
    e.preventDefault();
    const target = e.currentTarget;
    const index = parseInt(target.dataset.newItemsTabsIndexParam, 10);
    const label = target.dataset.newItemsTabsLabelParam || '';
    const url = target.dataset.newItemsTabsUrlParam || '#';

    this.activateIndex(index);
    this.updateCta(label, url);
    this.syncSelect(index);
    this.initializeSwiperForPanel(index);
  }

  /**
   * Handles change on mobile select dropdown.
   *
   * @param {Event} e - Change event from select element
   */
  switchSelect(e) {
    const selectedOption = e.target.options[e.target.selectedIndex];
    const index = parseInt(selectedOption.dataset.newItemsTabsIndexParam, 10);
    const label = selectedOption.dataset.newItemsTabsLabelParam || '';
    const url = selectedOption.dataset.newItemsTabsUrlParam || '#';

    this.activateIndex(index);
    this.updateCta(label, url);
    this.syncTabs(index);
    this.initializeSwiperForPanel(index);
  }

  /**
   * Activates the tab and panel at the given index.
   *
   * @param {number} index - Zero-based index of tab/panel to activate
   */
  activateIndex(index) {
    // Toggle active class on tabs
    this.tabTargets.forEach((tab, i) => {
      tab.classList.toggle('tabs__button--active', i === index);
    });

    // Toggle active class on panels
    this.panelTargets.forEach((panel, i) => {
      panel.classList.toggle('new-on-lastdodo__panel--active', i === index);
    });
  }

  /**
   * Gets the Swiper configuration for this section.
   * Matches the config previously in swipers.js for consistency.
   *
   * @returns {Object} Swiper configuration object
   */
  getSwiperConfig() {
    const isMobile = document.body.classList.contains('mobile-app');

    return {
      slidesPerView: 2.25,
      slidesPerGroup: 2,
      spaceBetween: 16,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation: {
        nextEl: '.new-on-lastdodo-swiper-next',
        prevEl: '.new-on-lastdodo-swiper-prev',
      },
      keyboard: {
        enabled: true,
      },
      lazy: {
        loadPrevNext: true,
      },
      breakpoints: isMobile ? {
        576: {
          slidesPerView: 4,
          slidesPerGroup: 3,
        },
        768: {
          slidesPerView: 6,
          slidesPerGroup: 5,
        },
        992: {
          slidesPerView: 8,
          slidesPerGroup: 7,
        },
      } : {
        576: {
          slidesPerView: 3.25,
          slidesPerGroup: 3,
        },
        768: {
          slidesPerView: 4.25,
          slidesPerGroup: 4,
        },
        992: {
          slidesPerView: 6.25,
          slidesPerGroup: 5,
        },
        1200: {
          slidesPerView: 7.25,
          slidesPerGroup: 6,
        },
      },
    };
  }

  /**
   * Initializes or updates Swiper for the active panel.
   * Creates new Swiper instance if not exists, otherwise resets to first slide.
   *
   * @param {number} index - Index of the active panel
   */
  initializeSwiperForPanel(index) {
    const panel = this.panelTargets[index];
    if (!panel) return;

    const swiperContainer = panel.querySelector('.swiper-container');
    if (!swiperContainer) return;

    // If Swiper already exists for this panel, reset to first slide
    if (this.swipers[index]) {
      // Reset to first slide (instant, no animation)
      this.swipers[index].slideTo(0, 0);
      // Update to recalculate dimensions and load lazy images
      this.swipers[index].update();
      if (this.swipers[index].lazy) {
        this.swipers[index].lazy.load();
      }
      return;
    }

    // Create new Swiper instance with panel-specific navigation
    const config = this.getSwiperConfig();

    // Override navigation to use panel-specific navigation buttons
    const prevEl = panel.querySelector('.new-on-lastdodo-swiper-prev');
    const nextEl = panel.querySelector('.new-on-lastdodo-swiper-next');

    if (prevEl && nextEl) {
      config.navigation = {
        prevEl: prevEl,
        nextEl: nextEl,
      };
    }

    // Create and store the Swiper instance
    // Swiper automatically manages swiper-button-disabled class on navigation
    this.swipers[index] = new Swiper(swiperContainer, config);
  }

  /**
   * Updates the CTA button text and URL based on current selection.
   *
   * @param {string} label - Label text to include in button (e.g., "Te koop")
   * @param {string} url - URL for the CTA link
   */
  updateCta(label, url) {
    if (this.hasCtaTextTarget) {
      // Use translation placeholder format
      const template = this.ctaTextTarget.dataset.template || 'Bekijk alle {label} items';
      this.ctaTextTarget.textContent = template.replace('{label}', label);
    }

    if (this.hasCtaTarget && url) {
      this.ctaTarget.href = url;
    }
  }

  /**
   * Syncs the mobile select dropdown with the active tab index.
   *
   * @param {number} index - Index to set on select
   */
  syncSelect(index) {
    if (this.hasSelectTarget) {
      this.selectTarget.selectedIndex = index;
    }
  }

  /**
   * Syncs the desktop tabs with the active select index.
   *
   * @param {number} index - Index of active tab
   */
  syncTabs(index) {
    this.tabTargets.forEach((tab, i) => {
      tab.classList.toggle('tabs__button--active', i === index);
    });
  }
}
